import { useLazyQuery } from "@apollo/client";
import { MagnifyingGlassIcon, UsersIcon } from "@heroicons/react/24/outline";
import {
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import EmptyState from "../../components/EmptyState/EmptyState";
import { ErrorAlert } from "../../components/ErrorAlert/ErrorAlert";
import Loader from "../../components/Loader/Loader";
import Page from "../../components/Page/Page";
import Panel from "../../components/Panel/Panel";
import Badge from "../../components/Styled/Badge";
import { StyledLink } from "../../components/Styled/StyledLink";
import TextInput from "../../components/TextInput/TextInput";
import { PagingResult, User } from "../../graphql/graphql";
import { USERS } from "../../graphql/queries-gql";
import { formatDateDayOnly } from "../../util/dates";

const PAGE_SIZE = 10;

const SearchBox = styled.div`
  width: 320px;
  max-width: 100%;
`;

const ClickableRow = styled(TableRow)`
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${(props) => props.theme.surfaceHover};
  }

  &:last-child td {
    border-bottom: 0;
  }
`;

const IdLink = styled(StyledLink)`
  font-family: ${(props) => props.theme.monoFontFamily};
  font-size: 12.5px;
`;

const NumberCell = styled(TableCell)`
  font-variant-numeric: tabular-nums;
`;

const Footer = styled.div`
  display: flex;
  justify-content: center;
`;

const UsersPage = () => {
  const [fetchUsers, { loading, error }] = useLazyQuery(USERS);
  const [page, setPage] = useState<PagingResult | undefined>(undefined);
  const [users, setUsers] = useState<User[]>([]);
  const [regex, setRegex] = useState("");
  const navigate = useNavigate();

  const handleSearch = (value: string) => {
    console.log("handleSearch " + value);
    setRegex(value);
    fetchUsers({
      variables: {
        regex: value,
        paging: {
          type: "page",
          pageArgs: {
            page: 1,
            limit: PAGE_SIZE,
          },
        },
      },
      onCompleted: (data) => {
        console.log("users onCompleted", data);
        setUsers(data.users.nodes);
        setPage(data.users.paging);
      },
      onError: (error) => {
        console.log("users onError", error);
      },
    });
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    fetchUsers({
      variables: {
        regex: regex,
        paging: {
          type: "page",
          pageArgs: {
            page: value,
            limit: PAGE_SIZE,
          },
        },
      },
      onCompleted: (data) => {
        console.log("users onCompleted", data);
        setUsers(data.users.nodes);
        setPage(data.users.paging);
      },
      onError: (error) => {
        console.log("users onError", error);
      },
    });
  };

  return (
    <Page
      title="Users"
      badge={page?.total != null && <Badge>{page.total} found</Badge>}
      toolbar={
        <SearchBox>
          <TextInput
            allowEmpty={true}
            onSubmit={(value) => handleSearch(value)}
            placeHolder={"Search users"}
            icon={<MagnifyingGlassIcon width={18} />}
          />
        </SearchBox>
      }
    >
      {error && <ErrorAlert error={error} />}

      {!page && !loading && !error && (
        <EmptyState
          Icon={UsersIcon}
          title="Search for a user"
          description="Type a search and press Enter. An empty search lists all users."
        />
      )}

      {page && !loading && !error && users.length === 0 && (
        <EmptyState Icon={UsersIcon} title="No users found" />
      )}

      {users.length > 0 && (
        <Panel padding={0}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>_id</TableCell>
                  <TableCell>Username</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Deleted</TableCell>
                  <TableCell>Banned</TableCell>
                  <TableCell>Banned until</TableCell>
                  <TableCell>Premium</TableCell>
                  <TableCell align="right">Credits</TableCell>
                  <TableCell align="right">TransTokens</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => {
                  return (
                    <ClickableRow
                      key={user._id}
                      onClick={() => navigate(`/user/${user._id}`)}
                    >
                      <TableCell component="th">
                        <IdLink
                          to={`/user/${user._id}`}
                          onClick={(event) => event.stopPropagation()}
                        >
                          {user._id}
                        </IdLink>
                      </TableCell>

                      <TableCell component="th">{user.username}</TableCell>
                      <TableCell component="th">{user.email}</TableCell>
                      <TableCell component="th">
                        {user.deleted ? <Badge $tone="red">Deleted</Badge> : ""}
                      </TableCell>
                      <TableCell component="th">
                        {user.banned ? <Badge $tone="red">Banned</Badge> : ""}
                      </TableCell>
                      <TableCell component="th">
                        {user.bannedUntilDate
                          ? formatDateDayOnly(user.bannedUntilDate)
                          : ""}
                      </TableCell>
                      <TableCell component="th">
                        {user.premium ? (
                          <Badge $tone="purple">Premium</Badge>
                        ) : (
                          ""
                        )}
                      </TableCell>
                      <NumberCell component="th" align="right">
                        {user.credits && user.credits > 0 ? user.credits : ""}
                      </NumberCell>
                      <NumberCell component="th" align="right">
                        {user.translationTokens && user.translationTokens > 0
                          ? user.translationTokens
                          : ""}
                      </NumberCell>
                    </ClickableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Panel>
      )}
      {page && page.page && page.totalPages && page.totalPages > 1 && (
        <Footer>
          <Pagination
            size="small"
            variant="outlined"
            count={page.totalPages}
            page={page.page}
            onChange={handlePageChange}
          />
        </Footer>
      )}
      {loading && <Loader />}
    </Page>
  );
};
export default UsersPage;
